import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Trash2, RotateCcw, XCircle, Star, Search, X, AlertTriangle } from "lucide-react";
import { getUrgency } from "./urgency";
import api, { formatApiError, formatEuro } from "../../lib/api";
import AdminShell from "./AdminShell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const STATUS_LABELS = {
  bozza: { label: "Bozza", color: "bg-slate-100 text-slate-700" },
  inviata: { label: "Inviata", color: "bg-blue-50 text-blue-700" },
  in_verifica: { label: "In verifica", color: "bg-amber-50 text-amber-700" },
  documenti_mancanti: { label: "Documenti mancanti", color: "bg-orange-50 text-orange-700" },
  contratto_approvato: { label: "Firma abilitata", color: "bg-indigo-50 text-indigo-800" },
  approvata: { label: "Approvata", color: "bg-green-50 text-green-800" },
  attestazione_inviata: { label: "Attestazione inviata", color: "bg-emerald-100 text-emerald-800" },
  respinta: { label: "Respinta", color: "bg-red-50 text-red-700" },
};

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [ade, setAde] = useState("all");
  const [urgency, setUrgency] = useState("all"); // "all" | "red" | "orange" | "yellow" | "green" | "min-4" | "min-2"
  const [q, setQ] = useState("");
  const [view, setView] = useState("active"); // "active" | "priority" | "trash"
  const [confirmAction, setConfirmAction] = useState(null); // { type, id, name }
  const [processing, setProcessing] = useState(false);
  // Reset dati di prova — richiede la digitazione esatta della frase di conferma
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const RESET_PHRASE = "CONFERMA RESET DATI";

  const runResetDati = async () => {
    if (resetPhrase !== RESET_PHRASE) return;
    setResetBusy(true);
    try {
      const r = await api.post("/admin/reset-dati-prova", { confirm: resetPhrase });
      toast.success(`Reset completato — eliminate ${r.data.deleted_submissions} pratiche`);
      setResetOpen(false);
      setResetPhrase("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  // Client-side filters (urgency + name search) derived from raw items
  const filteredItems = useMemo(() => {
    let list = items;
    // Text search on locatore / conduttore / agenzia name
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((s) => {
        const hay = [
          s.locatore_nome,
          s.conduttore_nome,
          s.locatore_email,
          s.richiedente_agenzia_nome,
        ]
          .filter(Boolean)
          .join(" | ")
          .toLowerCase();
        return hay.includes(needle);
      });
    }
    if (urgency === "all") return list;
    return list.filter((s) => {
      const u = getUrgency(s.submitted_at, s.status);
      if (!u) return false;
      switch (urgency) {
        case "red": return u.level === "red";
        case "orange": return u.level === "orange";
        case "yellow": return u.level === "yellow";
        case "green": return u.level === "green";
        case "min-4": return u.days >= 4;
        case "min-2": return u.days >= 2;
        default: return true;
      }
    });
  }, [items, urgency, q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status };
      if (ade !== "all") params.ade = ade;
      if (view === "trash") params.deleted = "only";
      if (view === "priority") params.priority = "only";
      const r = await api.get("/admin/submissions", { params });
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, ade, view]);

  const adeToInvoice = items.filter(
    (i) => i.registrazione_ade === true && !i.ade_fatturata,
  );
  const adeToInvoiceCount = adeToInvoice.length;
  const adeToInvoiceTotal = adeToInvoiceCount * 25;

  return (
    <AdminShell
      title="Richieste Attestazione"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setResetPhrase(""); setResetOpen(true); }}
            className="text-red-700 border-red-200 hover:bg-red-50"
            data-testid="reset-dati-btn"
          >
            <AlertTriangle size={14} className="mr-2" />
            Reset dati
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      }
    >
      {/* Tabs: Attive / Priority / Cestino */}
      <div className="mb-5 flex items-center gap-1 border-b border-slate-200">
        {[
          { v: "active", label: "Richieste attive" },
          { v: "priority", label: "Priority", icon: Star },
          { v: "trash", label: "Cestino", icon: Trash2 },
        ].map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setView(t.v)}
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              view === t.v
                ? t.v === "priority"
                  ? "border-amber-500 text-amber-700"
                  : "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
            data-testid={`tab-${t.v}`}
          >
            {t.icon && <t.icon size={14} className={view === t.v && t.v === "priority" ? "fill-amber-400 text-amber-500" : ""} />}
            {t.label}
          </button>
        ))}
      </div>

      {/* Priority — subtle summary banner on priority tab */}
      {view === "priority" && !loading && (
        <div
          className="w-full mb-5 flex items-center justify-between gap-4 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 px-5 py-4 shadow-md ring-1 ring-amber-600/20"
          data-testid="priority-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-white/40 flex items-center justify-center">
              <Star size={22} className="fill-amber-700 text-amber-800" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-amber-900 font-semibold">
                Sezione Priority — Associati Unioncasa
              </p>
              <p className="text-lg font-bold leading-tight">
                <span data-testid="priority-count">{filteredItems.length}</span>{" "}
                {filteredItems.length === 1 ? "pratica priority" : "pratiche priority"}
              </p>
              <p className="text-xs text-amber-900/80 mt-0.5">
                Gestisci con priorità queste richieste — clienti storici e agenzie partner.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AdE outstanding — only in Active view */}
      {view === "active" && adeToInvoiceCount > 0 && (
        <button
          type="button"
          onClick={() => setAde("to_invoice")}
          className="w-full mb-5 flex items-center justify-between gap-4 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-5 py-4 shadow-md ring-1 ring-amber-700/20 transition-colors text-left"
          data-testid="ade-outstanding-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-2xl">
              💰
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-amber-100 font-semibold">
                Registrazioni AdE da registrare
              </p>
              <p className="text-lg font-bold leading-tight">
                <span data-testid="ade-outstanding-count">{adeToInvoiceCount}</span>{" "}
                {adeToInvoiceCount === 1 ? "pratica" : "pratiche"} · totale € {adeToInvoiceTotal.toFixed(2).replace(".", ",")}
              </p>
              <p className="text-xs text-amber-50/90 mt-0.5">
                Clicca per filtrare — apri ogni pratica per marcarla come registrata.
              </p>
            </div>
          </div>
          <div className="flex-shrink-0 text-sm font-medium bg-white/15 rounded px-3 py-1.5">
            Vedi lista →
          </div>
        </button>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-64" data-testid="search-wrapper">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca locatore o conduttore…"
            className="w-full h-10 pl-8 pr-8 rounded-md border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
            data-testid="search-input"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Pulisci ricerca"
              data-testid="search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ade} onValueChange={setAde}>
          <SelectTrigger className="w-60" data-testid="ade-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="ade-filter-all">Registrazione AdE — tutte</SelectItem>
            <SelectItem value="to_invoice" data-testid="ade-filter-to-invoice">🔥 Da registrare (€ 25)</SelectItem>
            <SelectItem value="invoiced" data-testid="ade-filter-invoiced">✓ Già registrate</SelectItem>
            <SelectItem value="yes" data-testid="ade-filter-yes">Tutte con AdE €25</SelectItem>
            <SelectItem value="no" data-testid="ade-filter-no">Solo senza AdE</SelectItem>
            <SelectItem value="unanswered" data-testid="ade-filter-unanswered">Non risposto</SelectItem>
          </SelectContent>
        </Select>
        <Select value={urgency} onValueChange={setUrgency}>
          <SelectTrigger className="w-52" data-testid="urgency-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="urgency-filter-all">Urgenza — tutte</SelectItem>
            <SelectItem value="min-4" data-testid="urgency-filter-min4">🔥 Urgenti (4+ giorni)</SelectItem>
            <SelectItem value="min-2" data-testid="urgency-filter-min2">Attenzione (2+ giorni)</SelectItem>
            <SelectItem value="red" data-testid="urgency-filter-red">🔴 Solo rosso (5+ giorni)</SelectItem>
            <SelectItem value="orange" data-testid="urgency-filter-orange">🟠 Solo arancione (4 giorni)</SelectItem>
            <SelectItem value="yellow" data-testid="urgency-filter-yellow">🟡 Solo giallo (2-3 giorni)</SelectItem>
            <SelectItem value="green" data-testid="urgency-filter-green">🟢 Solo verde (≤1 giorno)</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-500">
          {filteredItems.length} {filteredItems.length === 1 ? "richiesta" : "richieste"}
          {(urgency !== "all" || q.trim()) && items.length !== filteredItems.length && (
            <span className="text-slate-400"> / {items.length} totali</span>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="submissions-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-medium">Locatore / Conduttore</th>
                <th className="px-4 py-3 font-medium">Indirizzo</th>
                <th className="px-4 py-3 font-medium">Zona</th>
                <th className="px-4 py-3 font-medium">Sub</th>
                <th className="px-4 py-3 font-medium">Canone (min – max)</th>
                <th className="px-4 py-3 font-medium">Stato</th>
                <th className="px-4 py-3 font-medium" title="In attesa da... (verde ≤1g, giallo 2-3g, arancio 4g, rosso 5+g)">Urgenza</th>
                <th className="px-4 py-3 font-medium text-center" title="Attestazione firmata caricata">Att.</th>
                <th className="px-4 py-3 font-medium text-center" title="Registrazione AdE € 25 richiesta dal locatore">AdE €25</th>
                <th className="px-4 py-3 font-medium">Aggiornata</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-500">Caricamento...</td></tr>
              )}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-500">Nessuna richiesta trovata</td></tr>
              )}
              {!loading && filteredItems.map((s) => {
                const status = STATUS_LABELS[s.status] || { label: s.status, color: "bg-slate-100 text-slate-700" };
                const urg = getUrgency(s.submitted_at, s.status);
                return (
                  <tr
                    key={s.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${s.is_priority ? "bg-amber-50/40" : ""}`}
                    data-testid={`row-${s.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900">{s.locatore_nome || "—"}</div>
                        {s.is_priority && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-1.5 py-0.5"
                            title={`Cliente Priority — associato: ${s.priority_matched_name || "n.d."}`}
                            data-testid={`priority-badge-${s.id}`}
                          >
                            <Star size={10} className="fill-amber-500 text-amber-600" />
                            Priority
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{s.locatore_email}</div>
                      {s.conduttore_nome && (
                        <div
                          className="mt-1 flex items-center gap-1.5 text-xs text-slate-600"
                          data-testid={`conduttore-cell-${s.id}`}
                        >
                          <span className="text-slate-400">→</span>
                          <span className="uppercase tracking-wider text-[9px] font-semibold text-slate-400">Cond.</span>
                          <span className="text-slate-800">{s.conduttore_nome}</span>
                        </div>
                      )}
                      {s.richiedente_tipo === "agenzia" ? (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5"
                          title={`Compilata da Professionista incaricato — ${s.richiedente_agenzia_nome || "—"}${s.richiedente_agenzia_referente ? " · " + s.richiedente_agenzia_referente : ""} · ${s.richiedente_email || "—"}`}
                          data-testid={`richiedente-badge-${s.id}`}
                        >
                          <span>🏢</span>
                          {s.richiedente_agenzia_nome ? `Prof. — ${s.richiedente_agenzia_nome}` : "Professionista incaricato"}
                        </div>
                      ) : s.richiedente_tipo === "locatore" ? (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5"
                          title="Compilata direttamente dal locatore"
                          data-testid={`richiedente-badge-${s.id}`}
                        >
                          <span>👤</span>
                          Locatore
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{s.indirizzo || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{s.zona || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{s.sub_fascia || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 tabular-nums">
                      {s.canone_minimo_mensile ? (
                        <>
                          {formatEuro(s.canone_minimo_mensile)} – {formatEuro(s.canone_massimo_mensile)}
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {urg ? (
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${urg.pillClass}`}
                          title={urg.tooltip}
                          data-testid={`urgency-${urg.level}-${s.id}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${urg.dotClass}`} />
                          {urg.label}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs" data-testid={`urgency-none-${s.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {s.has_final_attestation ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium text-green-800"
                          title="Attestazione firmata caricata"
                          data-testid={`attest-yes-${s.id}`}
                        >
                          ✓
                        </span>
                      ) : (
                        <span className="text-slate-300" data-testid={`attest-no-${s.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {s.registrazione_ade === true ? (
                        s.ade_fatturata ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold"
                            title="Servizio AdE già registrato"
                            data-testid={`ade-invoiced-${s.id}`}
                          >
                            ✓ registrata
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-bold shadow-sm"
                            title="AdE € 25 da registrare — clicca sulla riga per aprire e marcare come registrata"
                            data-testid={`ade-yes-${s.id}`}
                          >
                            € 25 · da registrare
                          </span>
                        )
                      ) : s.registrazione_ade === false ? (
                        <span className="text-slate-400 text-xs" title="Il locatore procede autonomamente" data-testid={`ade-no-${s.id}`}>
                          no
                        </span>
                      ) : (
                        <span className="text-slate-300" data-testid={`ade-unanswered-${s.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(s.updated_at).toLocaleString("it-IT")}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/submissions/${s.id}`}
                          className="inline-flex items-center gap-1 text-sm text-slate-900 hover:text-green-800"
                          data-testid={`open-${s.id}`}
                        >
                          Apri <ExternalLink size={12} />
                        </Link>
                        {view === "active" ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setConfirmAction({ type: "trash", id: s.id, name: s.locatore_nome || s.indirizzo || s.id });
                            }}
                            className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-700 transition-colors"
                            title="Sposta nel cestino"
                            data-testid={`trash-${s.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setConfirmAction({ type: "restore", id: s.id, name: s.locatore_nome || s.indirizzo || s.id });
                              }}
                              className="p-1.5 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-700 transition-colors"
                              title="Ripristina dal cestino"
                              data-testid={`restore-${s.id}`}
                            >
                              <RotateCcw size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setConfirmAction({ type: "permanent", id: s.id, name: s.locatore_nome || s.indirizzo || s.id });
                              }}
                              className="p-1.5 rounded hover:bg-red-100 text-red-600 hover:text-red-800 transition-colors"
                              title="Elimina definitivamente"
                              data-testid={`permanent-${s.id}`}
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <AlertDialogContent data-testid="trash-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "trash" && "Sposta nel cestino"}
              {confirmAction?.type === "restore" && "Ripristina dal cestino"}
              {confirmAction?.type === "permanent" && "Eliminazione definitiva"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "trash" && (
                <>
                  Vuoi spostare nel cestino la richiesta di <strong>{confirmAction?.name}</strong>?
                  Potrai ripristinarla in qualsiasi momento dalla sezione Cestino.
                </>
              )}
              {confirmAction?.type === "restore" && (
                <>Ripristina la richiesta di <strong>{confirmAction?.name}</strong> tra le pratiche attive?</>
              )}
              {confirmAction?.type === "permanent" && (
                <>
                  <span className="text-red-800 font-semibold">Attenzione — azione irreversibile.</span> La
                  richiesta di <strong>{confirmAction?.name}</strong> e tutti i suoi file caricati verranno
                  cancellati definitivamente. Non potrà essere recuperata.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={processing}
              data-testid="trash-confirm-ok"
              className={
                confirmAction?.type === "permanent"
                  ? "bg-red-700 hover:bg-red-800 text-white"
                  : confirmAction?.type === "restore"
                  ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                  : "bg-slate-900 hover:bg-slate-800 text-white"
              }
              onClick={async () => {
                if (!confirmAction) return;
                setProcessing(true);
                try {
                  if (confirmAction.type === "trash") {
                    await api.post(`/admin/submissions/${confirmAction.id}/trash`);
                    toast.success("Richiesta spostata nel cestino");
                  } else if (confirmAction.type === "restore") {
                    await api.post(`/admin/submissions/${confirmAction.id}/restore`);
                    toast.success("Richiesta ripristinata");
                  } else if (confirmAction.type === "permanent") {
                    await api.delete(`/admin/submissions/${confirmAction.id}/permanent`);
                    toast.success("Richiesta eliminata definitivamente");
                  }
                  setConfirmAction(null);
                  load();
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setProcessing(false);
                }
              }}
            >
              {processing ? "Attendi…" : (
                confirmAction?.type === "trash" ? "Sposta nel cestino"
                : confirmAction?.type === "restore" ? "Ripristina"
                : "Elimina definitivamente"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset dati di prova — hard delete di tutte le pratiche */}
      <AlertDialog open={resetOpen} onOpenChange={(v) => { if (!v) { setResetOpen(false); setResetPhrase(""); } }}>
        <AlertDialogContent data-testid="reset-dati-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle size={18} /> Reset dati di prova
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                <p>
                  Questa azione <strong>elimina definitivamente tutte le pratiche</strong> presenti
                  nel sistema (attive, nel cestino e priority). Gli utenti e gli account admin
                  non vengono toccati.
                </p>
                <p className="text-red-700 font-medium">
                  L&apos;azione non è reversibile. Usala solo per ripulire dati di prova prima di
                  andare in produzione.
                </p>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Per confermare, digita esattamente: <code className="bg-slate-100 px-1 rounded">{RESET_PHRASE}</code>
                  </label>
                  <input
                    type="text"
                    value={resetPhrase}
                    onChange={(e) => setResetPhrase(e.target.value)}
                    placeholder={RESET_PHRASE}
                    autoComplete="off"
                    className="w-full h-10 px-3 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 text-sm font-mono"
                    data-testid="reset-dati-phrase"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-dati-cancel" disabled={resetBusy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => { e.preventDefault(); runResetDati(); }}
              disabled={resetPhrase !== RESET_PHRASE || resetBusy}
              data-testid="reset-dati-confirm"
            >
              {resetBusy ? "Eliminazione…" : "Elimina tutte le pratiche"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
